import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoadingCOA from "../LoadingCOA/LoadingCOA";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import { useTutorial } from "../../context/tutorialContext";
import HeaderSelect from "../HeaderSelect/HeaderSelect";
import "./Header.css";
import apiClient from "../../api/apiClient";
import Notifications from "../Notifications/Notifications";
import ThemeToggle from "../ThemeToggle/ThemeToggle";

const BuildingIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <path d="M2.5 14V3a1 1 0 011-1h5a1 1 0 011 1v11M9.5 14V7a1 1 0 011-1h3a1 1 0 011 1v7M1 14h14M4.5 4.5h2M4.5 7h2M4.5 9.5h2M11.5 8.5h1M11.5 11h1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
);
 
const MapPinIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <path d="M8 1.5A4.5 4.5 0 0112.5 6c0 3.5-4.5 8.5-4.5 8.5S3.5 9.5 3.5 6A4.5 4.5 0 018 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
);

const Header = ({ userId, userProfile, setHeaderAndMenu, headerTitle, coaProcessing }) => {
    const { setCurrentLicense, currentLicense, setLicensesLoaded } = useLicense();
    const { currentLocation, setCurrentLocation, locations, refreshLocations } = useLocation_();
    const { tutorialStatus, active: tutorialActive, start, currentPhase } = useTutorial();
    const navigate = useNavigate();
    const [licenses, setLicenses] = useState([]);
    const [title, setTitle] = useState("");

    const getUserInfo = async () => {
        try {
            const response = await apiClient.get(`/user/get-user-license-info`, {
                headers: { id: userId },
            });
            const currentUser = response.data;
            setLicenses(currentUser.licenses);

            if (currentUser.licenses.length > 0) {
                const freshLicense = currentLicense
                    ? currentUser.licenses.find(l => l.licenseNo === currentLicense.licenseNo) || currentUser.licenses[0]
                    : currentUser.licenses[0];
                setCurrentLicense(freshLicense);
                setTitle(freshLicense.title);
                localStorage.setItem("currentLicense", JSON.stringify(freshLicense));
                refreshLocations(freshLicense.licenseNo);
            }
            setLicensesLoaded(true);
        } catch (error) {
            console.error("Error fetching user info:", error);
            setLicensesLoaded(true);
        }
    };

    useEffect(() => {
        if (userId) getUserInfo();
    }, [userId]);

    // License options
    const licenseOptions = licenses.map((l) => ({ value: l.licenseNo, label: l.company }));
    const licenseValue = currentLicense
        ? { value: currentLicense.licenseNo, label: currentLicense.company }
        : null;

    const handleLicenseChange = (option) => {
        const selectedLicense = licenses.find((l) => l.licenseNo === option.value);
        if (!selectedLicense) return;
        sessionStorage.setItem("price_min", null);
        sessionStorage.setItem("price_max", null);
        sessionStorage.setItem("showroom_max_total_terpenes", null);
        sessionStorage.setItem("showroom_min_total_terpenes", null);
        sessionStorage.setItem("showroom_max_total_cannabinoids", null);
        sessionStorage.setItem("showroom_min_total_cannabinoids", null);
        sessionStorage.setItem("showroom_selected_product_types", null);
        sessionStorage.setItem("showroom_selected_type", null);
        sessionStorage.setItem("showroom_terpenes_options", null);
        sessionStorage.setItem("showroom_cannabinoids_options", null);
        sessionStorage.setItem("showroom_selected_colors", null);
        sessionStorage.setItem("searchTerm", "");
        sessionStorage.setItem("showroom_product_or_operators", "products");
        sessionStorage.setItem("filteredColor", "");
        sessionStorage.setItem("filteredProductType", "");
        setCurrentLocation(null);
        setCurrentLicense(selectedLicense);
        setTitle(selectedLicense.title);
        localStorage.setItem("currentLicense", JSON.stringify(selectedLicense));
        refreshLocations(selectedLicense.licenseNo);
    };

    // Location options
    const isScoped = currentLicense?.location_scoped === 1;
    const allLocationsLabel = currentLicense?.company || "All Locations";

    const locationOptions = locations.length > 0
        ? [
            ...(isScoped ? [] : [{ value: "all", label: allLocationsLabel }]),
            ...locations.map((l) => ({ value: l.locationId, label: l.locationName })),
          ]
        : [];

    const locationValue = currentLocation
        ? { value: currentLocation.locationId, label: currentLocation.locationName }
        : isScoped && locations[0]
            ? { value: locations[0].locationId, label: locations[0].locationName }
            : locations.length > 0
                ? { value: "all", label: allLocationsLabel }
                : null;

    const handleLocationChange = (option) => {
        if (option.value === "all") {
            setCurrentLocation(null);
        } else {
            const loc = locations.find((l) => l.locationId === option.value);
            if (loc) {
                sessionStorage.setItem("searchTerm", ""); 
                sessionStorage.setItem("filteredColor", "");
                sessionStorage.setItem("filteredProductType", "");
                setCurrentLocation(loc); 
            }
        }
    };

    return (
        <div className="header"> 
            <div className="header-logo-and-title">
                <img src="/DYC-icon-ColorWheel.png" alt="DYC Logo" className="logo-for-header" />
                <h2 className="header-message">{headerTitle}</h2>
            </div>
            <div className="header-buttons">
                {coaProcessing && <div className="button-container"><LoadingCOA /></div>}
                <button
                    className="header-tutorial-btn"
                    onClick={() => start(currentPhase)}
                    disabled={tutorialActive}
                    aria-label="Start tutorial"
                    title="App Tutorial"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    {tutorialStatus !== "completed" && tutorialStatus !== "skipped" && (
                        <span className="header-tutorial-btn__indicator" />
                    )}
                </button>
                <span data-tutorial="header-theme-toggle"><ThemeToggle /></span>
                <span data-tutorial="notifications-bell"><Notifications /></span>

                {(userProfile === "admin" || userProfile === "superadmin") && (
                    <span data-tutorial="header-license-select">
                        <HeaderSelect
                            options={licenseOptions}
                            value={licenseValue}
                            onChange={handleLicenseChange}
                            placeholder="Select License"
                            icon={<BuildingIcon />}
                        />
                    </span>
                )}

                {locationOptions.length > 0 ? (
                    <span data-tutorial="header-location-select">
                        <HeaderSelect
                            options={locationOptions}
                            value={locationValue}
                            onChange={handleLocationChange}
                            searchable
                            searchPlaceholder="Search locations..."
                            placeholder={allLocationsLabel}
                            icon={<MapPinIcon />}
                        />
                    </span>
                ) : (
                    <button
                        className="header-create-location"
                        onClick={() => navigate("/settings/company/locations?newLocation=true")}
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        Create Location
                    </button>
                )}
            </div>
        </div>
    );
};

export default Header;
